export const COMPANY = {
  name: "Wuhan FullEnviro Solutions Co., Ltd.",
  shortName: "FullEnviro",
  email: "sales@fullenviro.com",
  whatsappDisplay: "+86 130 2630 1989",
  whatsappNumber: "8613026301989",
  location: "Wuhan, China",
  domain: "https://www.fullenvirosolutions.com",
};

export const ROUTES = {
  home: "/",
  highPurity: "/solutions/high-purity-water",
  roEdi: "/solutions/compact-ro-edi",
  polishing: "/solutions/polishing-point-of-use-filtration",
  specialty: "/solutions/specialty-membrane-separation",
  replacement: "/solutions/membrane-replacement-retrofit",
  electronics: "/applications/electronics-photonics",
  pharma: "/applications/pharmaceutical-biotechnology",
  food: "/applications/food-fermentation",
  chemicals: "/applications/specialty-chemicals-industrial",
  products: "/products",
  engineering: "/engineering-support",
  pilot: "/engineering-support/pilot-testing-scale-up",
  resources: "/resources",
  about: "/about",
  contact: "/contact",
  privacy: "/privacy",
  terms: "/terms",
};

export const ROUTE_ALIASES = {
  "/membrane-solutions": "replacement",
  "/solutions/specialty-separation": "specialty",
  "/advanced-industrial-components": "electronics",
  "/advanced-industrial-components/photonics-optical-components": "electronics",
  "/cioe-2026-sourcing-support": "resources",
  "/services": "engineering",
  "/applications": "electronics",
  "/specialty-separation": "specialty",
  "/commissioning": "engineering",
};

const meta = (title, description) => ({ title, description });

export const routeMeta = {
  home: meta(
    "High-Purity Water & Membrane Separation Solutions | FullEnviro",
    "Engineering-led high-purity water, RO-EDI polishing, point-of-use filtration, specialty MF/UF separation and membrane replacement support."
  ),
  highPurity: meta(
    "High-Purity Water Systems & Process Support | FullEnviro",
    "Compact high-purity water systems, pretreatment, RO, EDI, polishing, monitoring and distribution support for critical industrial processes."
  ),
  roEdi: meta(
    "Compact RO-EDI High-Purity Water Systems | FullEnviro",
    "Application-based compact RO-EDI and double-pass RO systems for laboratories, electronics, photonics, pharmaceutical and specialty chemical processes."
  ),
  polishing: meta(
    "Polishing & Point-of-Use Filtration for High-Purity Water | FullEnviro",
    "Mixed-bed polishing, UV TOC reduction, membrane degassing, final UF and point-of-use filtration for high-purity process water."
  ),
  specialty: meta(
    "Specialty MF/UF Membrane Separation | FullEnviro",
    "MF/UF clarification, concentration, product recovery and process-liquid separation supported by screening, pilot testing and scale-up."
  ),
  replacement: meta(
    "Membrane Replacement & Retrofit Support | FullEnviro",
    "Compatibility review and retrofit support for MBR, UF, NF, RO, EDI and process filtration components based on real operating conditions."
  ),
  electronics: meta(
    "High-Purity Water for Electronics & Photonics | FullEnviro",
    "High-purity water, polishing and point-of-use filtration for electronics, optical manufacturing, packaging, R&D and precision cleaning."
  ),
  pharma: meta(
    "Process Water & Membrane Separation for Pharma and Biotech | FullEnviro",
    "Process-water modules and specialty membrane separation support for pharmaceutical, biotechnology and life-science applications."
  ),
  food: meta(
    "Membrane Separation for Food & Fermentation | FullEnviro",
    "MF/UF clarification, concentration, recovery and pilot support for fermentation broths, food streams and process liquids."
  ),
  chemicals: meta(
    "High-Purity Water & Separation for Specialty Chemicals | FullEnviro",
    "High-purity dilution water, filtration and specialty membrane separation for chemicals and critical industrial processes."
  ),
  products: meta(
    "Membranes, EDI, Resins & Process Filtration Products | FullEnviro",
    "Selected MBR, UF, NF, RO, EDI, ion-exchange resin, filter and high-purity process components with engineering-led selection support."
  ),
  engineering: meta(
    "Engineering, Selection & Commissioning Support | FullEnviro",
    "Process-data review, product selection, technical proposals, FAT coordination, export delivery, commissioning and troubleshooting support."
  ),
  pilot: meta(
    "Membrane Pilot Testing & Scale-Up Support | FullEnviro",
    "Structured membrane screening, pilot testing, acceptance criteria and scale-up support for specialty separation applications."
  ),
  resources: meta(
    "High-Purity Water & Membrane Technical Resources | FullEnviro",
    "Practical selection guides and engineering notes for RO-EDI, high-purity water, membrane replacement and specialty MF/UF projects."
  ),
  about: meta(
    "About FullEnviro | Engineering-Led Technical Export Support",
    "FullEnviro connects industrial process requirements with qualified membrane, high-purity water and filtration supply from China."
  ),
  contact: meta(
    "Submit a High-Purity Water or Membrane Inquiry | FullEnviro",
    "Send water analysis, process data, membrane models or project requirements for an engineering-led review."
  ),
  privacy: meta("Privacy Policy | FullEnviro", "How FullEnviro handles business inquiry and contact information."),
  terms: meta(
    "Terms & Technical Disclaimer | FullEnviro",
    "Technical information, validation boundaries and supplier-role terms for FullEnviro services."
  ),
};

export const coreSolutions = [
  {
    route: "highPurity",
    number: "01",
    eyebrow: "Primary growth line",
    title: "High-Purity Water",
    text: "Compact RO-EDI, double-pass RO, polishing, point-of-use filtration and water-quality control for critical processes.",
    tags: ["RO / EDI", "Polishing", "POU filtration"],
  },
  {
    route: "specialty",
    number: "02",
    eyebrow: "Differentiated engineering",
    title: "Specialty Membrane Separation",
    text: "MF/UF screening, clarification, concentration, recovery and scale-up support built around the real process stream.",
    tags: ["MF / UF", "Pilot testing", "Scale-up"],
  },
  {
    route: "replacement",
    number: "03",
    eyebrow: "Established capability",
    title: "Replacement & Retrofit",
    text: "Compatibility review for membranes, EDI and filtration components based on dimensions, duty and operating risk.",
    tags: ["MBR / UF / NF / RO", "EDI", "Retrofit"],
  },
];

export const applications = [
  {
    route: "electronics",
    title: "Electronics & Photonics",
    text: "High-purity water and final filtration for optical cleaning, packaging, electronics production and R&D lines.",
    cue: "Precision cleaning • packaging • R&D",
  },
  {
    route: "pharma",
    title: "Pharmaceutical & Biotechnology",
    text: "Process-water modules, polishing and validation-led membrane separation for life-science applications.",
    cue: "Process water • biotech separation",
  },
  {
    route: "food",
    title: "Food & Fermentation",
    text: "MF/UF clarification, concentration and product recovery supported by stream testing and pilot data.",
    cue: "Clarification • concentration • recovery",
  },
  {
    route: "chemicals",
    title: "Specialty Chemicals & Industry",
    text: "High-purity dilution water, process filtration, recovery and difficult industrial separation duties.",
    cue: "Dilution water • filtration • reuse",
  },
];

export const engineeringSteps = [
  { number: "01", title: "Review", text: "Confirm feed data, duty, output target and project constraints." },
  { number: "02", title: "Select", text: "Compare process routes, components and qualified supply options." },
  { number: "03", title: "Verify", text: "Use compatibility checks, samples, pilots or FAT where the risk requires it." },
  { number: "04", title: "Deliver", text: "Coordinate technical documents, packing, export and commissioning support." },
];

export const productGroups = [
  {
    title: "Membrane Products",
    items: ["MBR modules", "UF and MF modules", "NF and RO elements", "Specialty hollow-fiber modules"],
  },
  {
    title: "High-Purity Water Components",
    items: ["EDI modules", "Mixed-bed resins", "UV and degassing modules", "Final UF and POU filters"],
  },
  {
    title: "Process Filtration",
    items: ["Cartridge filters", "Filter housings", "High-purity filtration", "Application-specific prefiltration"],
  },
  {
    title: "System & Retrofit Support",
    items: ["Compact RO-EDI systems", "Polishing skids", "Replacement review", "Custom module integration"],
  },
];

export const solutionPages = {
  highPurity: {
    eyebrow: "High-purity water solutions",
    title: "Build the water quality around the process—not a generic equipment list.",
    lead: "FullEnviro supports compact and modular high-purity water projects from feed review through RO, EDI, polishing, final filtration and operating handover.",
    category: "high-purity",
    metrics: [
      ["System scope", "Pretreatment to point of use"],
      ["Typical scale", "Laboratory to small production"],
      ["Project basis", "Measured feed and target quality"],
    ],
    sections: [
      ["Pretreatment & RO", "Protect downstream equipment and establish a stable ionic and organic load before final purification."],
      ["EDI & polishing", "Match continuous deionization, mixed-bed polishing and final treatment to the actual quality target."],
      ["Final quality control", "Review resistivity, TOC, silica, particles, microbiology and dissolved gases where relevant."],
      ["Storage & distribution", "Consider recirculation, materials, sanitization, redundancy and point-of-use conditions early."],
    ],
    checklistTitle: "Useful project data",
    checklist: ["Source-water analysis", "Required flow and operating hours", "Target quality at the point of use", "Application and critical contaminants", "Storage and distribution scope", "Project location and delivery target"],
    boundary: "Our initial focus is compact systems, modules, polishing and process support for laboratories, packaging, photonics, electronics and specialty manufacturing—not turnkey advanced-wafer-fab EPC claims.",
    related: ["roEdi", "polishing", "electronics"],
  },
  roEdi: {
    eyebrow: "Compact RO-EDI systems",
    title: "A practical high-purity platform for laboratories and small production lines.",
    lead: "Double-pass RO and RO-EDI systems can provide a robust foundation when pretreatment, recovery, concentrate handling and final polishing are designed as one process.",
    category: "high-purity",
    metrics: [["Core train", "RO • RO-EDI • polishing"], ["Configuration", "Skid or modular"], ["Support", "Selection through commissioning"]],
    sections: [
      ["Feed conditioning", "Screen hardness, alkalinity, organics, silica, oxidants and variability before selecting the RO train."],
      ["RO configuration", "Choose single-pass, double-pass and recovery targets from feed risk and downstream quality—not habit."],
      ["EDI integration", "Confirm CO₂ load, hardness leakage, feed conductivity and operating window before sizing the EDI stage."],
      ["Controls & documentation", "Define monitoring points, alarms, interlocks, FAT records and operator handover requirements."],
    ],
    checklistTitle: "Selection questions",
    checklist: ["What is the full feed-water analysis?", "What quality is required and where?", "What is the hourly and daily demand?", "Is hot-water or chemical sanitization required?", "What redundancy is needed?", "Who owns installation and commissioning?"],
    boundary: "Performance guarantees require agreed feed limits, sampling points, operating conditions and an acceptance method.",
    related: ["highPurity", "polishing", "engineering"],
  },
  polishing: {
    eyebrow: "Polishing & point-of-use",
    title: "Protect final water quality after the primary purification train.",
    lead: "The last treatment steps often determine whether water remains suitable at the tool or process connection. We coordinate polishing and final-control components as a defined duty.",
    category: "high-purity",
    metrics: [["Functions", "Ionic • organic • particle control"], ["Position", "Loop or point of use"], ["Format", "Modules and compact skids"]],
    sections: [
      ["Ionic polishing", "Mixed-bed resin and EDI options for final ionic removal and stable resistivity."],
      ["Organic control", "UV oxidation and supporting treatment where TOC reduction is a defined project requirement."],
      ["Gas management", "Membrane degassing and process review when dissolved oxygen or carbon dioxide matters."],
      ["Final filtration", "UF and cartridge filtration selected around particle target, flow, materials and replacement strategy."],
    ],
    checklistTitle: "Define the point of use",
    checklist: ["Required quality at the tool", "Distance and loop design", "Peak and normal flow", "Allowable pressure drop", "Materials and connection standard", "Monitoring and filter-change criteria"],
    boundary: "Instrument, filter and component claims are confirmed against the selected manufacturer's documented range and the project's validation plan.",
    related: ["highPurity", "roEdi", "products"],
  },
  specialty: {
    eyebrow: "Specialty membrane separation",
    title: "Move from membrane screening to a defensible process window.",
    lead: "For fermentation, food, biotechnology and industrial process liquids, membrane choice should follow the separation goal, stream behavior, cleaning constraints and evidence from testing.",
    category: "separation",
    metrics: [["Functions", "Clarify • concentrate • recover"], ["Scale", "Lab • pilot • production"], ["Method", "Data-led validation"]],
    sections: [
      ["Clarification", "Remove cells, biomass, suspended solids or colloidal material while protecting the desired product."],
      ["Concentration", "Evaluate UF molecular-weight cut-off, flux, recovery and diafiltration needs for the real stream."],
      ["Process-liquid recovery", "Review membrane material, chemical exposure, temperature and cleanability for industrial duties."],
      ["Scale-up", "Translate pilot flux, recovery, cleaning and batch time into a commercial module and system basis."],
    ],
    checklistTitle: "Data before recommendation",
    checklist: ["Feed composition and sample availability", "Target permeate or retained product", "Batch volume or continuous flow", "pH, temperature and solvents", "Recovery and concentration target", "Cleaning and sanitation constraints"],
    boundary: "Sensitive or regulated duties require agreed testing, documentation and customer validation before any performance claim.",
    related: ["pilot", "food", "pharma"],
  },
  replacement: {
    eyebrow: "Replacement & retrofit",
    title: "Compare the complete operating fit—not only the nominal model.",
    lead: "FullEnviro reviews membrane, EDI and filtration replacements against dimensions, interfaces, duty, cleaning, control limits and the operational reason for change.",
    category: "replacement",
    metrics: [["Products", "MBR • UF • NF • RO • EDI"], ["Projects", "Replacement • retrofit"], ["Evidence", "Dimensions • data • duty"]],
    sections: [
      ["Physical compatibility", "Confirm envelope, ports, connections, pressure ratings, orientation and service access."],
      ["Process compatibility", "Compare flux, recovery, rejection, air scour, pressure, cleaning and expected feed variability."],
      ["Control compatibility", "Check instrumentation, power, signals, alarms and operating sequence for modules or skids."],
      ["Change risk", "Document differences, validation steps, spares, warranty boundaries and the reason for replacement."],
    ],
    checklistTitle: "Send what you have",
    checklist: ["Existing brand and model", "Nameplate and installation photos", "Drawings and connection dimensions", "Feed and operating data", "Current performance or failure mode", "Quantity and replacement deadline"],
    boundary: "Compatibility is confirmed case by case. We do not claim universal or unsupported ‘100% equivalent’ replacement.",
    related: ["products", "engineering", "contact"],
  },
  pilot: {
    eyebrow: "Pilot testing & scale-up",
    title: "Use a small, controlled test to answer the questions that matter at scale.",
    lead: "A good pilot is designed around acceptance criteria, material balance and cleaning—not simply whether permeate appears clear.",
    category: "separation",
    metrics: [["Start", "Stream and objective"], ["Measure", "Flux • recovery • quality"], ["Output", "Scale-up basis"]],
    sections: [
      ["Test design", "Define candidate membranes, operating range, sample points and acceptance criteria before the run."],
      ["Operating evidence", "Record normalized flux, pressure, temperature, recovery, product passage and concentration behavior."],
      ["Cleaning review", "Evaluate recoverability, chemical compatibility, rinse endpoint and practical cycle time."],
      ["Scale-up package", "Document assumptions, recommended design flux, membrane area, batch time and remaining risks."],
    ],
    checklistTitle: "Pilot inputs",
    checklist: ["Representative sample", "Target product and loss limits", "Analytical methods", "Operating window", "Cleaning chemicals", "Commercial-scale objective"],
    boundary: "Pilot results apply to the tested stream and conditions. Scale-up assumptions and uncertainty are stated explicitly.",
    related: ["specialty", "food", "engineering"],
  },
};

export const applicationPages = {
  electronics: {
    eyebrow: "Electronics & photonics",
    title: "High-purity water for precision cleaning, packaging and R&D.",
    lead: "Support for applications that need consistent ionic, organic and particle control without overstating full advanced-fab capability.",
    category: "high-purity",
    uses: ["Optical component cleaning", "Semiconductor packaging and test", "Electronics production", "Laboratory and R&D lines"],
    priorities: ["Define quality at the actual point of use", "Control particles after storage and distribution", "Match materials and sanitization to the process", "Plan monitoring, spares and service from the start"],
    related: ["highPurity", "polishing", "roEdi"],
  },
  pharma: {
    eyebrow: "Pharmaceutical & biotechnology",
    title: "Process-water and membrane support with validation boundaries made clear.",
    lead: "Modular water treatment and separation support for research, process development and production duties where documentation and change control matter.",
    category: "high-purity",
    uses: ["Process and laboratory water", "Bioprocess clarification", "Concentration and diafiltration", "Utility and pretreatment support"],
    priorities: ["Confirm applicable quality and regulatory requirements", "Define product-contact materials", "Agree sanitization and cleaning strategy", "Separate equipment capability from site validation responsibility"],
    related: ["highPurity", "specialty", "pilot"],
  },
  food: {
    eyebrow: "Food & fermentation",
    title: "Clarification and concentration built around yield, flux and cleanability.",
    lead: "MF/UF process development for fermentation broths, food streams and valuable process liquids, from sample screening through scale-up.",
    category: "separation",
    uses: ["Fermentation broth clarification", "Biomass and solids removal", "Protein or enzyme concentration", "Product and process-water recovery"],
    priorities: ["Protect product yield and quality", "Measure flux decline and recovery", "Validate cleaning against the real foulant", "Translate batch time and recovery into economics"],
    related: ["specialty", "pilot", "engineering"],
  },
  chemicals: {
    eyebrow: "Specialty chemicals & industrial processes",
    title: "High-purity dilution water, filtration and selective process recovery.",
    lead: "Engineering support for chemical dilution water, difficult industrial streams and process-liquid separation where material compatibility is central.",
    category: "high-purity",
    uses: ["High-purity chemical dilution", "Process-water polishing", "Solution recovery and recycle", "Oily or emulsified stream treatment"],
    priorities: ["Confirm chemical exposure and materials", "Define contaminants that affect the process", "Review safety and operating controls", "Test uncertain separations before scale-up"],
    related: ["highPurity", "specialty", "products"],
  },
};

export const resources = [
  {
    type: "Selection guide",
    title: "Double-Pass RO or RO-EDI?",
    text: "A practical comparison for high-purity dilution water and small production systems.",
  },
  {
    type: "Project checklist",
    title: "Data Required for a High-Purity Water System",
    text: "The feed, demand, quality, distribution and acceptance information needed before design.",
  },
  {
    type: "Compatibility guide",
    title: "How to Review a Membrane Replacement",
    text: "Dimensions, process duty, cleaning and control details that determine whether a replacement is credible.",
  },
  {
    type: "Engineering note",
    title: "From Membrane Screening to Pilot Scale-Up",
    text: "How to structure testing for specialty clarification and concentration duties.",
  },
];
